(() => {
    if (customElements.get('cut-configurator')) return;

    class CutConfigurator extends HTMLElement {
      static async addStockToCart({ config, result, button, selectedValues }) {
        // A detached instance reuses the exact standard order/PDF/shipping methods without mounting a second UI.
        const order = document.createElement('cut-configurator');
        order.config = { ...config };
        order.config.labels = order.getLabels();
        order.config.backendLabels = order.getBackendLabels();
        order.result = result;
        order.addCartButton = button;
        order.getSelectedFilterValues = () => ({ ...selectedValues });
        order.renderEmpty = message => { throw new Error(message); };
        await order.addToCart();
      }

      connectedCallback() {
        this.config = JSON.parse(this.querySelector('[data-config]').textContent);
        this.config.labels = this.getLabels();
        this.config.backendLabels = this.getBackendLabels();
        this.profileSelect = this.querySelector('[data-profile-select]');
        this.optionFields = this.querySelector('[data-option-fields]');
        this.rowsContainer = this.querySelector('[data-cut-rows]');
        this.addRowButton = this.querySelector('[data-add-row]');
        this.addCartButton = this.querySelector('[data-add-cart]');
        this.message = this.querySelector('[data-message]');
        this.summary = this.querySelector('[data-summary]');
        this.bar = this.querySelector('[data-bar]');
        this.printButton = this.querySelector('[data-print-plan]');
        this.modeSwitch = this.querySelector('[data-mode-select]');
        this.deliveryKicker = this.querySelector('[data-cut-delivery-kicker]');
        this.deliveryTitle = this.querySelector('[data-cut-delivery-title]');
        this.deliveryText = this.querySelector('[data-cut-delivery-text]');
        this.deliveryNote = this.querySelector('[data-cut-delivery-note]');
        this.result = null;

        window.FibroluxCutDelivery.init(this);
        this.applyLabels();
        if (this.modeSwitch && this.config.labels?.switchAria) this.modeSwitch.setAttribute('aria-label', this.config.labels.switchAria);

        this.config.profiles = this.config.profiles
          .map((profile) => this.prepareProfile(profile))
          .filter((profile) => profile.variants.length);

        this.renderProfiles();
        this.addRow();
        this.bindEvents();
        this.syncPlanViewerState();
        this.prefillStockSuggestion();
        this.calculate();
        this.renderDelivery();
      }

      syncPlanViewerState() {
        const hasPlanParam = (() => {
          try {
            return new URLSearchParams(window.location.search || '').has('plan');
          } catch (error) {
            return false;
          }
        })();

        if (this.printButton) {
          this.printButton.hidden = !hasPlanParam;
          this.printButton.style.display = hasPlanParam ? '' : 'none';
        }
        if (this.printButton && !this.printButton.__fibroluxBound) {
          this.printButton.__fibroluxBound = true;
          this.printButton.addEventListener('click', () => window.print());
        }
      }

      prefillStockSuggestion() {
        const params = new URLSearchParams(window.location.search || '');
        if (params.get('source') !== 'stock-suggestion' || params.get('mode') !== 'profile' || params.has('plan')) return false;
        const quantity = Number(params.get('stock_quantity'));
        if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100) return false;
        const index = this.config.profiles.findIndex(p => p.handle === params.get('product'));
        if (index < 0) return false;
        const profile = this.config.profiles[index];
        const variant = profile.variants.find(v => String(v.id) === params.get('stock_variant'));
        if (!variant?.length || variant.length < Number(this.config.minLength || 1)) return false;
        this.profileSelect.value = String(index);
        this.renderOptionFields();
        this.optionFields.querySelectorAll('[data-option-filter]').forEach(select => {
          select.value = variant.options[Number(select.dataset.optionFilter)] || '';
        });
        this.updateOptionAvailability();
        const row = this.rowsContainer.querySelector('[data-row]');
        if (!row) return false;
        window.FibroluxCutUnits.setMillimetres(row.querySelector('[data-length]'), variant.length);
        row.querySelector('[data-quantity]').value = String(quantity);
        // Only the explicit product-page link selects this mode; all prices/stock/plans are recalculated.
        this.deliveryPreference = 'fastest';
        return true;
      }

      encodePlanCode(payload) {
        try {
          const json = JSON.stringify(payload || {});
          const base64 = btoa(unescape(encodeURIComponent(json)));
          return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
        } catch (error) {
          return '';
        }
      }

      buildPlanLink(code) {
        let base = String(this.config?.planViewerUrl || '').trim();
        if (!code) return '';
        if (!base) base = `${window.location.origin}${window.location.pathname}`;
        if (base.startsWith('/')) base = `${window.location.origin}${base}`;
        const sep = base.includes('?') ? '&' : '?';
        return `${base}${sep}plan=${encodeURIComponent(code)}`;
      }

      loadPlan(plan) {
        const payload = plan && typeof plan === 'object' ? plan : null;
        if (!payload) return false;

        const handle = String(payload.profileHandle || payload.handle || '').trim();
        const cuts = Array.isArray(payload.cuts) ? payload.cuts : [];
        const selectedValues = payload.selectedValues && typeof payload.selectedValues === 'object' ? payload.selectedValues : {};

        if (handle && Array.isArray(this.config?.profiles)) {
          const index = this.config.profiles.findIndex((p) => String(p?.handle || '') === handle);
          if (index >= 0 && this.profileSelect) {
            this.profileSelect.value = String(index);
            this.renderOptionFields();
          }
        }

        if (this.optionFields) {
          const selects = Array.from(this.optionFields.querySelectorAll('[data-option-filter]'));
          selects.forEach((select) => {
            const idx = String(select.dataset.optionFilter || '');
            if (!idx) return;
            const val = selectedValues[idx];
            if (val !== undefined && val !== null) select.value = String(val);
          });
          this.updateOptionAvailability();
        }

        if (this.rowsContainer) {
          this.rowsContainer.innerHTML = '';
          (cuts.length ? cuts : [{ length: 0, quantity: 1 }]).forEach((cut) => {
            this.addRow();
            const row = this.rowsContainer.querySelector('[data-row]:last-child');
            if (!row) return;
            const len = row.querySelector('[data-length]');
            const qty = row.querySelector('[data-quantity]');
            window.FibroluxCutUnits.setMillimetres(len, Math.max(0, Number(cut.length || 0)));
            if (qty) qty.value = String(Math.max(1, Number(cut.quantity || 1)));
          });
        }

        this.calculate();
        return true;
      }

      addDays(days) {
        return window.FibroluxCutDelivery.addDeliveryDays(days);
      }

      formatShortDate(iso) {
        if (!iso) return '';
        const [year, month, day] = String(iso).split('-');
        if (!year || !month || !day) return iso;
        const date = new Date(Number(year), Number(month) - 1, Number(day));
        if (Number.isNaN(date.getTime())) return iso;

        const locale = String(this.config.language || document.documentElement.lang || 'de');
        try {
          return new Intl.DateTimeFormat(locale, {
            month: '2-digit',
            day: '2-digit',
          }).format(date);
        } catch (error) {
          return `${day}.${month}.`;
        }
      }

      getCutDeliveryEstimateRange() {
        if (!this.result?.bars?.length) return null;

        const requiresOrder = !window.FibroluxCutDelivery.stocked(this.result);

        const {fromDays,toDays} = window.FibroluxCutDelivery.profileDeliveryDays(this.result);
        const startIso = this.addDays(fromDays);
        const endIso = this.addDays(toDays);

        return {
          fromDays,
          toDays,
          startIso,
          endIso,
          dateText: `${this.formatShortDate(startIso)}-${this.formatShortDate(endIso)}`,
          requiresOrder,
        };
      }

      renderDelivery() {
        if (!this.deliveryText) return;
        const labels = this.config.labels;

        if (this.deliveryKicker) this.deliveryKicker.textContent = labels.deliveryKicker || 'Lieferzeit';
        if (this.deliveryTitle) this.deliveryTitle.textContent = labels.deliveryTitle || 'Voraussichtliche Lieferung';

        const estimate = this.getCutDeliveryEstimateRange();

        if (!this.result) {
          this.deliveryText.textContent = labels.deliveryPlaceholder || 'Bitte Profil auswählen, um die Lieferzeit zu sehen.';
          if (this.deliveryNote) {
            this.deliveryNote.textContent = '';
            this.deliveryNote.hidden = true;
          }
          return;
        }

        this.deliveryText.textContent = estimate?.dateText || '';

        if (this.deliveryNote) {
          this.deliveryNote.textContent = '';
          this.deliveryNote.hidden = true;
        }
      }

      getLabels() {
        const translations = {
          de: {
            profile: 'Profil',
            selectProfile: 'Profil auswählen',
            chooseSuffix: 'auswählen',
            cuts: 'Zuschnitte',
            addLength: 'Weitere Länge',
            lengthPlaceholder: 'Länge in mm',
            quantity: 'Menge',
            removeCut: 'Zuschnitt entfernen',
            addToCart: 'Mit Plan-PDF in den Warenkorb',
            deliveryKicker: 'Lieferzeit',
            deliveryTitle: 'Voraussichtliche Lieferung',
            deliveryPlaceholder: 'Bitte Profil auswählen, um die Lieferzeit zu sehen.',
            orderedCutsHeading: 'Bestellte Zuschnitte',
            messageSelectProfile: 'Bitte ein Profil auswählen.',
            messageSelectOption: 'Bitte auswählen',
            messageEnterCut: 'Bitte mindestens eine Zuschnittlänge eingeben.',
            messageMinLength: 'Mindestlänge je Zuschnitt',
            messageNoFit: 'Diese Zuschnitte passen in keine verfügbare Rohlänge.',
            messageNoLengths: 'Erkannte Rohlängen',
            messageMissingService: 'Bitte das Serviceprodukt S1010 (Erstschnitt/Folgeschnitt) in den Section-Einstellungen hinterlegen (oder Variant IDs eintragen).',
            messageCartError: 'Der Warenkorb konnte nicht aktualisiert werden. Bitte versuche es erneut.',
            printPlan: 'Plan drucken',
            modeGratings: 'Gitterroste',
            modeProfiles: 'Profile',
            switchAria: 'Konfigurator umschalten',
            cutView: 'Zuschnittansicht',
            rest: 'Rest',
            kerfLoss: 'Schnittverlust',
            per: 'je',
            unusedTotal: 'Nicht genutzte Länge',
          },
          en: {
            profile: 'Profile',
            selectProfile: 'Select profile',
            chooseSuffix: 'select',
            cuts: 'Cuts',
            addLength: 'Add length',
            lengthPlaceholder: 'Length in mm',
            quantity: 'Quantity',
            removeCut: 'Remove cut',
            addToCart: 'Add to cart with plan PDF',
            deliveryKicker: 'Delivery time',
            deliveryTitle: 'Estimated delivery',
            deliveryPlaceholder: 'Select a profile to see the delivery time.',
            orderedCutsHeading: 'Ordered cuts',
            messageSelectProfile: 'Please select a profile.',
            messageSelectOption: 'Please select',
            messageEnterCut: 'Please enter at least one cut length.',
            messageMinLength: 'Minimum cut length',
            messageNoFit: 'These cuts do not fit into any available raw length.',
            messageNoLengths: 'Detected raw lengths',
            messageMissingService: 'Please configure the S1010 service product (first/additional cut) in the section settings (or provide variant IDs).',
            messageCartError: 'Cart could not be updated. Please try again.',
            printPlan: 'Print plan',
            modeGratings: 'Gratings',
            modeProfiles: 'Profiles',
            switchAria: 'Switch configurator',
            cutView: 'Cut view',
            rest: 'Rest',
            kerfLoss: 'Kerf loss',
            per: 'per',
            unusedTotal: 'Unused length',
          },
          fr: {
            profile: 'Profil',
            selectProfile: 'Choisir un profil',
            chooseSuffix: 'choisir',
            cuts: 'Découpes',
            addLength: 'Ajouter une longueur',
            lengthPlaceholder: 'Longueur en mm',
            quantity: 'Quantité',
            removeCut: 'Supprimer la découpe',
            addToCart: 'Ajouter au panier avec le plan PDF',
            deliveryKicker: 'Délai de livraison',
            deliveryTitle: 'Livraison estimée',
            deliveryPlaceholder: 'Choisissez un profil pour voir le délai de livraison.',
            orderedCutsHeading: 'Découpes commandées',
            messageSelectProfile: 'Veuillez choisir un profil.',
            messageSelectOption: 'Veuillez sélectionner',
            messageEnterCut: 'Veuillez saisir au moins une longueur de découpe.',
            messageMinLength: 'Longueur minimale par découpe',
            messageNoFit: 'Ces découpes ne rentrent dans aucune longueur brute disponible.',
            messageNoLengths: 'Longueurs brutes détectées',
            messageMissingService:
              'Veuillez configurer le produit de service S1010 (coupe initiale/supplémentaire) dans les paramètres de la section (ou saisir les IDs de variante).',
            messageCartError: 'Le panier n’a pas pu être mis à jour. Veuillez réessayer.',
            printPlan: 'Imprimer le plan',
            modeGratings: 'Caillebotis',
            modeProfiles: 'Profils',
            switchAria: 'Changer de configurateur',
            cutView: 'Vue de coupe',
            rest: 'Reste',
            kerfLoss: 'Perte de coupe',
            per: 'par',
            unusedTotal: 'Longueur inutilisée',
          },
          es: {
            profile: 'Perfil',
            selectProfile: 'Seleccionar perfil',
            chooseSuffix: 'seleccionar',
            cuts: 'Cortes',
            addLength: 'Añadir longitud',
            lengthPlaceholder: 'Longitud en mm',
            quantity: 'Cantidad',
            removeCut: 'Eliminar corte',
            addToCart: 'Añadir al carrito con el plano PDF',
            deliveryKicker: 'Plazo de entrega',
            deliveryTitle: 'Entrega estimada',
            deliveryPlaceholder: 'Selecciona un perfil para ver el plazo de entrega.',
            orderedCutsHeading: 'Cortes pedidos',
            messageSelectProfile: 'Selecciona un perfil.',
            messageSelectOption: 'Por favor, selecciona',
            messageEnterCut: 'Introduce al menos una longitud de corte.',
            messageMinLength: 'Longitud mínima por corte',
            messageNoFit: 'Estos cortes no caben en ninguna longitud en bruto disponible.',
            messageNoLengths: 'Longitudes en bruto detectadas',
            messageMissingService:
              'Configura el producto de servicio S1010 (primer/siguiente corte) en los ajustes de la sección (o introduce los IDs de variante).',
            messageCartError: 'No se pudo actualizar el carrito. Inténtalo de nuevo.',
            printPlan: 'Imprimir plano',
            modeGratings: 'Rejillas',
            modeProfiles: 'Perfiles',
            switchAria: 'Cambiar configurador',
            cutView: 'Vista de corte',
            rest: 'Resto',
            kerfLoss: 'Pérdida de corte',
            per: 'por',
            unusedTotal: 'Longitud sin usar',
          },
          it: {
            profile: 'Profilo',
            selectProfile: 'Seleziona profilo',
            chooseSuffix: 'seleziona',
            cuts: 'Tagli',
            addLength: 'Aggiungi lunghezza',
            lengthPlaceholder: 'Lunghezza in mm',
            quantity: 'Quantità',
            removeCut: 'Rimuovi taglio',
            addToCart: 'Aggiungi al carrello con il piano PDF',
            deliveryKicker: 'Tempi di consegna',
            deliveryTitle: 'Consegna stimata',
            deliveryPlaceholder: 'Seleziona un profilo per vedere i tempi di consegna.',
            orderedCutsHeading: 'Tagli ordinati',
            messageSelectProfile: 'Seleziona un profilo.',
            messageSelectOption: 'Seleziona',
            messageEnterCut: 'Inserisci almeno una lunghezza di taglio.',
            messageMinLength: 'Lunghezza minima per taglio',
            messageNoFit: 'Questi tagli non entrano in nessuna lunghezza grezza disponibile.',
            messageNoLengths: 'Lunghezze grezze rilevate',
            messageMissingService:
              'Configura il prodotto di servizio S1010 (primo/taglio aggiuntivo) nelle impostazioni della sezione (o inserisci gli ID variante).',
            messageCartError: 'Impossibile aggiornare il carrello. Riprova.',
            printPlan: 'Stampa piano',
            modeGratings: 'Grigliati',
            modeProfiles: 'Profili',
            switchAria: 'Cambia configuratore',
            cutView: 'Vista taglio',
            rest: 'Resto',
            kerfLoss: 'Perdita di taglio',
            per: 'per',
            unusedTotal: 'Lunghezza non utilizzata',
          },
          pl: {
            profile: 'Profil',
            selectProfile: 'Wybierz profil',
            chooseSuffix: 'wybierz',
            cuts: 'Cięcia',
            addLength: 'Dodaj długość',
            lengthPlaceholder: 'Długość w mm',
            quantity: 'Ilość',
            removeCut: 'Usuń cięcie',
            addToCart: 'Dodaj do koszyka z planem PDF',
            deliveryKicker: 'Czas dostawy',
            deliveryTitle: 'Szacowana dostawa',
            deliveryPlaceholder: 'Wybierz profil, aby zobaczyć czas dostawy.',
            orderedCutsHeading: 'Zamówione cięcia',
            messageSelectProfile: 'Wybierz profil.',
            messageSelectOption: 'Wybierz',
            messageEnterCut: 'Wprowadź co najmniej jedną długość cięcia.',
            messageMinLength: 'Minimalna długość cięcia',
            messageNoFit: 'Te cięcia nie pasują do żadnej dostępnej długości surowej.',
            messageNoLengths: 'Wykryte długości surowe',
            messageMissingService:
              'Skonfiguruj produkt usługowy S1010 (pierwsze/kolejne cięcie) w ustawieniach sekcji (lub wpisz ID wariantu).',
            messageCartError: 'Nie udało się zaktualizować koszyka. Spróbuj ponownie.',
            printPlan: 'Drukuj plan',
            modeGratings: 'Kraty',
            modeProfiles: 'Profile',
            switchAria: 'Przełącz konfigurator',
            cutView: 'Podgląd cięcia',
            rest: 'Reszta',
            kerfLoss: 'Strata cięcia',
            per: 'na',
            unusedTotal: 'Niewykorzystana długość',
          },
        };

        const language = String(this.config.language || document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
        return translations[language] || translations.de;
      }

      getBackendLabels() {
        return {
          cutOrder: 'Zuschnittauftrag',
          belongsTo: 'Gehört zu',
          profile: 'Profil',
          article: 'Artikel',
          rawItemsHeading: 'Rohmaterial',
          orderedCutsHeading: 'Bestellte Zuschnitte',
          sumsHeading: 'Summen',
          planHeading: 'Sägeplan',
          noteHeading: 'Hinweis',
          piecesIncluded: 'Reststücke werden mitgeliefert.',
          totalCuts: 'Schnitte gesamt',
          kerfLoss: 'Schnittverlust',
          totalRest: 'Rest gesamt',
          unusedTotal: 'Nicht genutzte Länge',
          selectedRawLength: 'Rohlänge',
          cuts: 'Zuschnitte',
          cutPlanProperty: 'Zuschnittplan',
          cutsTotalProperty: 'Schnitte gesamt',
          seeFirstCut: 'siehe Erstschnitt',
          cartAttributeCutOrder: 'Bestellung mit Zuschnitt',
          cartAttributeLastCutOrder: 'Letzter Zuschnittauftrag',
          cartAttributeDeliveryTime: 'Lieferzeit (Zuschnitt)',
        };
      }

      applyLabels() {
        this.querySelectorAll('[data-label-text]').forEach((element) => {
          const key = element.dataset.labelText;
          if (this.config.labels[key]) element.textContent = this.config.labels[key];
        });
        this.querySelectorAll('[data-label-aria]').forEach((element) => {
          const key = element.dataset.labelAria;
          if (this.config.labels[key]) element.setAttribute('aria-label', this.config.labels[key]);
        });
      }

      bindEvents() {
        this.profileSelect.addEventListener('change', () => {
          this.renderOptionFields();
          this.calculate();
        });

        this.optionFields.addEventListener('change', (event) => {
          if (event.target.matches('[data-option-filter]')) this.updateOptionAvailability();
          this.calculate();
        });

        this.addRowButton.addEventListener('click', () => {
          this.addRow();
          this.calculate();
        });

        this.rowsContainer.addEventListener('input', () => this.calculate());
        this.rowsContainer.addEventListener('change', () => this.calculate());
        this.rowsContainer.addEventListener('click', (event) => {
          const removeButton = event.target.closest('[data-remove-row]');
          if (!removeButton) return;
          const row = removeButton.closest('[data-row]');
          if (row) row.remove();
          if (!this.rowsContainer.querySelector('[data-row]')) this.addRow();
          this.calculate();
        });

        this.addCartButton.addEventListener('click', () => this.addToCart());
      }

      renderProfiles() {
        this.config.profiles.forEach((profile, index) => {
          const option = document.createElement('option');
          option.value = String(index);
          option.textContent = profile.title;
          this.profileSelect.append(option);
        });
      }

      renderOptionFields() {
        const profile = this.getSelectedProfile();
        this.optionFields.innerHTML = '';

        if (!profile) {
          return;
        }

        profile.filters.forEach((filter) => {
          const field = document.createElement('div');
          field.className = 'cut-configurator__field';
          field.innerHTML = `
            <label class="cut-configurator__label">${filter.name}</label>
            <select class="cut-configurator__select" data-option-filter="${filter.index}">
              <option value="">${filter.name} ${this.config.labels.chooseSuffix || 'auswählen'}</option>
              ${filter.values.map((value) => `<option value="${this.escapeHtml(value)}">${this.escapeHtml(value)}</option>`).join('')}
            </select>
          `;
          this.optionFields.append(field);
        });

        this.updateOptionAvailability();
      }

      updateOptionAvailability() {
        const profile = this.getSelectedProfile();
        if (!profile) return;

        const selects = Array.from(this.optionFields.querySelectorAll('[data-option-filter]'));
        const selected = this.getSelectedFilterValues();

        selects.forEach((select) => {
          const index = select.dataset.optionFilter;
          Array.from(select.options).forEach((option) => {
            if (!option.value) return;

            const possible = profile.variants.some((variant) =>
              variant.options?.[Number(index)] === option.value &&
              Object.entries(selected).every(([selectedIndex, selectedValue]) =>
                selectedIndex === index || !selectedValue || variant.options?.[Number(selectedIndex)] === selectedValue
              )
            );

            option.disabled = !possible;
          });

          if (select.value && select.selectedOptions[0]?.disabled) {
            select.value = '';
          }
        });
      }

      getSelectedProfile() {
        const rawValue = this.profileSelect?.value;
        if (!rawValue) return null;
        const index = Number(rawValue);
        if (Number.isNaN(index)) return null;
        return this.config.profiles[index] || null;
      }

      getSelectedFilters(profile) {
        if (!profile) return null;
        const selected = this.getSelectedFilterValues();

        const missing = profile.filters.find((filter) => !selected[String(filter.index)]);

        return {
          values: selected,
          missing,
        };
      }

      getSelectedFilterValues() {
        const selected = {};

        this.optionFields.querySelectorAll('[data-option-filter]').forEach((select) => {
          selected[select.dataset.optionFilter] = select.value;
        });

        return selected;
      }

      getSelectedOptionLabels(profile, selected) {
        return Object.entries(selected)
          .map(([index, value]) => ({
            name: profile.optionNames?.[Number(index)] || 'Option',
            value,
          }))
          .filter((option) => option.value);
      }

      prepareProfile(profile) {
        const optionCount = Math.max(
          profile.optionNames?.length || 0,
          ...profile.variants.map((variant) => variant.options?.length || 0)
        );
        const optionNames = Array.from({ length: optionCount }, (_, index) => profile.optionNames?.[index] || `Option ${index + 1}`);
        const lengthOptionIndex = optionNames.findIndex((name) => this.isLengthOptionName(name));

        const variants = profile.variants
          .map((variant) => {
            const length =
              lengthOptionIndex >= 0
                ? this.extractLength({ title: variant.options?.[lengthOptionIndex] || '', options: [] })
                : this.extractLength(variant);
            return { ...variant, length };
          })
          .filter((variant) => variant.length)
          .sort((a, b) => a.length - b.length);

        const filters = optionNames
          .map((name, index) => ({ name, index }))
          .filter((option) => option.index !== lengthOptionIndex)
          .map((option) => ({
            ...option,
            values: Array.from(new Set(variants.map((variant) => variant.options?.[option.index]).filter(Boolean))),
          }))
          .filter((option) => option.values.length > 1);

        return {
          ...profile,
          optionNames,
          filters,
          variants,
        };
      }

      addRow() {
        const row = document.createElement('div');
        row.className = 'cut-configurator__row';
        row.dataset.row = '';
        row.innerHTML = `
          <label class="cut-configurator__field">
            <span class="visually-hidden">${this.escapeHtml(this.config.labels.lengthPlaceholder || 'Länge in mm')}</span>
            <input class="cut-configurator__input" type="number" min="${this.config.minLength}" step="1" inputmode="numeric" placeholder="${this.escapeHtml(this.config.labels.lengthPlaceholder || 'Länge in mm')}" data-length>
          </label>
          <label class="cut-configurator__field">
            <span class="visually-hidden">${this.escapeHtml(this.config.labels.quantity || 'Menge')}</span>
            <input class="cut-configurator__input" type="number" min="1" step="1" inputmode="numeric" value="1" data-quantity>
          </label>
          <button class="cut-configurator__remove" type="button" data-remove-row aria-label="${this.escapeHtml(this.config.labels.removeCut || 'Zuschnitt entfernen')}">
            <svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" style="display:block;">
              <path fill="currentColor" d="M9 3h6l1 2h5v2h-2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7H3V5h5l1-2Zm8 4H7v14h10V7Zm-7 3h2v8h-2v-8Zm4 0h2v8h-2v-8Z"/>
            </svg>
          </button>
        `;
        this.rowsContainer.append(row);
        window.FibroluxCutUnits.prepare(row);
      }

      extractLength(variant) {
        const source = [variant.title, ...(variant.options || [])].join(' ');
        const millimeter = source.match(/(\d[\d\s.,]*)\s*mm\b/i);
        if (millimeter) return Math.round(this.parseMeasurement(millimeter[1], 'mm'));
        const meter = source.match(/(\d[\d\s.,]*)\s*m\b/i);
        if (meter) return Math.round(this.parseMeasurement(meter[1], 'm') * 1000);
        return null;
      }

      isLengthOptionName(name) {
        return /l(ä|ae|a)?nge|length|longitud|longueur|lunghezza|dlugosc|długość/i.test(String(name));
      }

      escapeHtml(value) {
        const element = document.createElement('div');
        element.textContent = value;
        return element.innerHTML;
      }

      parseMeasurement(value, unit) {
        const compact = String(value).replace(/\s/g, '');
        const hasOnlyThousandsSeparators = /^[0-9]{1,3}([.,][0-9]{3})+$/.test(compact);

        if (unit === 'mm' && hasOnlyThousandsSeparators) {
          return Number(compact.replace(/[.,]/g, ''));
        }

        if (unit === 'mm' && /^[0-9]+$/.test(compact)) {
          return Number(compact);
        }

        return Number(compact.replace(',', '.'));
      }

      getCuts() {
        const cuts = [];
        this.rowsContainer.querySelectorAll('[data-row]').forEach((row) => {
          const length = window.FibroluxCutUnits.toMillimetres(row.querySelector('[data-length]'));
          const quantity = Number(row.querySelector('[data-quantity]').value);
          if (!length && !quantity) return;
          if (length > 0 && quantity > 0) cuts.push({ length: Math.round(length), quantity: Math.round(quantity) });
        });
        return cuts;
      }

      optimizeCuts(pieces, variants) {
        // Same bounded planner for ordinary and stock-only choices; counts physical cuts, not ordered pieces.
        return window.FibroluxCutDelivery.profilePlan(this, pieces, variants);
      }

      optimizeCutsGreedy(pieces, stockVariants) {
        return window.FibroluxCutDelivery.profilePlan(this, pieces, stockVariants);
      }

      sortBars(bars) {
        return bars.sort((a, b) => a.used - b.used || a.pieces.length - b.pieces.length);
      }

      findVariantForUsed(used, variants) {
        return variants.find((variant) => variant.length >= used);
      }

      planBars(bars, variants) {
        return bars
          .map((bar) => {
            const variant = this.findVariantForUsed(bar.used, variants);
            if (!variant) return null;

            return {
              variant,
              pieces: bar.pieces.slice(),
              used: bar.used,
              kerfLoss: bar.pieces.length * Number(this.config.kerf),
              piecesLength: bar.pieces.reduce((sum, length) => sum + length, 0),
              rest: variant.length - bar.used,
            };
          })
          .filter(Boolean);
      }

      groupPlannedBars(bars) {
        const groups = new Map();

        bars.forEach((bar) => {
          const piecesKey = bar.pieces.slice().sort((a, b) => a - b).join(',');
          const key = `${bar.variant.id}|${piecesKey}|${bar.rest}|${bar.kerfLoss}`;

          if (!groups.has(key)) {
            groups.set(key, { ...bar, quantity: 0 });
          }

          groups.get(key).quantity += 1;
        });

        return Array.from(groups.values());
      }

      scoreBars(bars, variants) {
        const plannedBars = this.planBars(bars, variants);
        if (!plannedBars || plannedBars.length !== bars.length) return null;
        return this.scorePlannedBars(plannedBars);
      }

      scorePlannedBars(bars) {
        return {
          rawLength: bars.reduce((sum, bar) => sum + bar.variant.length * (bar.quantity || 1), 0),
          maxRest: Math.max(...bars.map((bar) => bar.rest), 0),
          price: bars.reduce((sum, bar) => sum + bar.variant.price * (bar.quantity || 1), 0),
          count: bars.reduce((sum, bar) => sum + (bar.quantity || 1), 0),
        };
      }

      compareScores(a, b) {
        return a.rawLength - b.rawLength || a.maxRest - b.maxRest || a.price - b.price || a.count - b.count;
      }

      formatCuts(cuts) {
        return cuts.map((cut) => `${cut.quantity} x ${cut.length} mm`).join(', ');
      }

      formatBars(bars) {
        const counts = bars.reduce((items, bar) => {
          const key = `${bar.variant.length} mm`;
          items[key] = (items[key] || 0) + (bar.quantity || 1);
          return items;
        }, {});

        return Object.entries(counts)
          .map(([length, quantity]) => `${quantity} x ${length}`)
          .join(', ');
      }

      formatNumber(number) {
        return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(number);
      }

      formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, this.config.moneyFormat);
        }

        return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          style: 'currency',
          currency: this.config.currency || 'EUR',
        }).format(cents / 100);
      }

      metric(label, value) {
        return `<div class="cut-configurator__metric"><span>${this.escapeHtml(label)}</span><strong>${this.escapeHtml(value)}</strong></div>`;
      }

      renderEmpty(text, type = '') {
        this.message.textContent = text;
        this.message.dataset.type = type;
        this.summary.innerHTML = '';
        this.bar.innerHTML = '';
        this.addCartButton.disabled = true;
      }

      renderBars(result) {
        this.bar.innerHTML = '';
        if (!result?.bars?.length) return;

        result.bars.forEach((bar) => {
          const container = document.createElement('div');
          container.className = 'cut-configurator__bar';

          const title = document.createElement('div');
          title.className = 'cut-configurator__bar-title';
          title.innerHTML = `<span>${this.escapeHtml(bar.variant.length)} mm</span><span>${this.escapeHtml(bar.quantity || 1)}×</span>`;

          const segments = document.createElement('div');
          segments.className = 'cut-configurator__segments';

          const total = Math.max(1, bar.variant.length);

          const pieces = bar.pieces || [];
          pieces.forEach((piece, index) => {
            segments.append(this.createSegment('piece', piece, total, `${piece} mm`));
            if (index < Number(bar.pieceCutCount ?? pieces.length)) segments.append(this.createSegment('kerf', Number(this.config.kerf), total, ''));
          });

          (bar.restPieces || [bar.rest]).filter(length=>length>0).forEach((length,index) => {
            if (index) segments.append(this.createSegment('kerf', Number(this.config.kerf), total, ''));
            segments.append(this.createSegment('rest', length, total, `${length} mm`));
          });

          container.append(title, segments);
          if (bar.restCuts) {
            const note = document.createElement('p');
            note.className = 'cut-configurator__bar-title';
            note.textContent = `${this.config.labels.rest || 'Rest'}: ${this.formatPieceCounts(bar.restPieces)} · ${this.config.labels.totalCuts || 'Schnitte'}: +${bar.restCuts} / ${bar.variant.length} mm`;
            container.append(note);
          }
          this.bar.append(container);
        });
      }

      createSegment(type, length, total, text) {
        const segment = document.createElement('div');
        segment.className = `cut-configurator__segment cut-configurator__segment--${type}`;
        segment.style.flexBasis = `${(length / total) * 100}%`;
        segment.title = text || '';
        if (text && type !== 'kerf' && length / total > 0.06) segment.textContent = text;
        return segment;
      }

      renderSummary(result) {
        if (!result) {
          this.summary.innerHTML = '';
          return;
        }

        this.summary.innerHTML = `
          ${this.metric('Rohmaterial', this.formatBars(result.bars))}
          ${this.metric(this.config.labels.orderedCutsHeading || 'Bestellte Zuschnitte', this.formatCuts(result.cuts))}
          ${this.metric(this.config.labels.unusedTotal || 'Nicht genutzte Länge', `${result.unused} mm (${this.formatNumber(result.wastePercent)} %)`)}
          ${this.metric('Material', this.formatMoney(result.profilePrice))}
          ${this.metric('Zuschnittkosten', result.cutCount ? this.formatMoney(result.servicePrice) : `${this.formatMoney(0)} · ${window.FibroluxCutDelivery.noCutLabel(this)}`)}
          ${this.metric('Gesamt', this.formatMoney(result.totalPrice))}
        `;
      }

      calculate() {
        const delivery = window.FibroluxCutDelivery;
        delivery.clear(this);
        const profile = this.getSelectedProfile();
        const selectedFilters = this.getSelectedFilters(profile);
        const cuts = this.getCuts();

        this.result = null;
        this.renderDelivery();

        if (window.FibroluxCutUnits.hasFractionalMillimetres(this)) {
          this.renderEmpty(window.FibroluxCutUnits.errorText(this), 'error');
          return;
        }

        if (!profile) {
          this.renderEmpty(this.config.labels.messageSelectProfile || 'Bitte ein Profil auswählen.');
          return;
        }

        if (selectedFilters?.missing) {
          this.renderEmpty(`${this.config.labels.messageSelectOption || 'Bitte auswählen'}: ${selectedFilters.missing.name}`);
          return;
        }

        if (!cuts.length) {
          this.renderEmpty(this.config.labels.messageEnterCut || 'Bitte mindestens eine Zuschnittlänge eingeben.');
          return;
        }

        const invalid = cuts.find((cut) => cut.length < Number(this.config.minLength) || cut.quantity < 1);
        if (invalid) {
          this.renderEmpty(`${this.config.labels.messageMinLength || 'Mindestlänge je Zuschnitt'}: ${this.config.minLength} mm.`, 'error');
          return;
        }

        const pieces = cuts.flatMap((cut) => Array.from({ length: cut.quantity }, () => cut.length));
        const matchingVariants = profile.variants.filter((candidate) => candidate.available === true &&
          Object.entries(selectedFilters.values).every(([index, value]) => candidate.options?.[Number(index)] === value)
        );
        let standard = this.optimizeCuts(pieces, matchingVariants);
        if (!delivery.orderable(standard)) standard = delivery.profileStockPlan(this, pieces, matchingVariants);
        let plan = standard;
        {
          const candidates = [standard, delivery.profileStockPlan(this, pieces, matchingVariants),
            ...matchingVariants.map(v => this.optimizeCuts(pieces, [v]))].filter(delivery.orderable);
          candidates.forEach(p => { p.totalPrice = delivery.profilePrice(p) + delivery.profileCutPrice(this,delivery.profileCutCount(p)); });
          candidates.sort(delivery.compare);
          const fastest = candidates.find(delivery.stocked) || null;
          plan = delivery.select(this, standard, fastest, candidates[0] || null);
        }

        if (!plan) {
          if (this.deliveryPreference === 'fastest') {
            this.renderEmpty(delivery.labels(this).noStock, 'error');
            delivery.render(this);
            return;
          }
          const availableLengths = matchingVariants.map((candidate) => `${candidate.length} mm`).join(', ');
          this.renderEmpty(`${this.config.labels.messageNoFit || 'Diese Zuschnitte passen in keine verfügbare Rohlänge.'} ${this.config.labels.messageNoLengths || 'Erkannte Rohlängen'}: ${availableLengths || '-'}.`, 'error');
          return;
        }

        const cutCount = delivery.profileCutCount(plan);
        const piecesLength = pieces.reduce((sum, length) => sum + length, 0);
        const kerfLoss = cutCount * Number(this.config.kerf);
        const rawLength = plan.bars.reduce((sum, bar) => sum + bar.variant.length * (bar.quantity || 1), 0);
        const rest = plan.bars.reduce((sum, bar) => sum + bar.rest * (bar.quantity || 1), 0);
        const unused = rawLength - piecesLength;
        const wastePercent = rawLength > 0 ? (unused / rawLength) * 100 : 0;

        const { firstVariant, additionalVariant } = this.resolveServiceVariants();
        if (cutCount > 0 && (!firstVariant || (cutCount > 1 && !additionalVariant))) {
          this.renderEmpty(this.config.labels.messageMissingService || 'Bitte das Serviceprodukt S1010 (Erstschnitt/Folgeschnitt) in den Section-Einstellungen hinterlegen (oder Variant IDs eintragen).', 'error');
          return;
        }

        const servicePrice = delivery.profileCutPrice(this,cutCount);
        const profilePrice = plan.bars.reduce((sum, bar) => sum + bar.variant.price * (bar.quantity || 1), 0);
        const totalPrice = profilePrice + servicePrice;

        this.result = {
          profile,
          selectedOptions: this.getSelectedOptionLabels(profile, selectedFilters.values),
          bars: plan.bars,
          cuts,
          pieces,
          cutCount,
          piecesLength,
          kerfLoss,
          rawLength,
          rest,
          unused,
          wastePercent,
          profilePrice,
          servicePrice,
          totalPrice,
        };

        this.message.textContent = '';
        this.addCartButton.disabled = false;
        delivery.annotate(this);
        delivery.offcutChoice(this);
        this.renderBars(this.result);
        this.renderSummary(this.result);
        delivery.render(this);
        this.renderDelivery();
      }

      getServiceVariant(id) {
        const numericId = Number(id);
        if (!numericId) return null;
        return (this.config.serviceVariants || []).find((variant) => Number(variant.id) === numericId) || null;
      }

      resolveServiceVariants() {
        const allVariants = Array.isArray(this.config.serviceVariants) ? this.config.serviceVariants : [];

        // If a single service product contains multiple variant sets (e.g. Profile + Gitterrost),
        // narrow down to the Profile variants first.
        const variants = (() => {
          if (allVariants.length <= 2) return allVariants;
          const needle = /profil/i;
          const sNeedle = /S1010/i;
          const filtered = allVariants.filter((variant) => needle.test(String(variant?.title || '')) || needle.test(String(variant?.sku || '')) || sNeedle.test(String(variant?.sku || '')));
          return filtered.length >= 2 ? filtered : allVariants;
        })();

        const firstFromSetting = this.getServiceVariant(this.config.firstCutVariantId);
        const additionalFromSetting = this.getServiceVariant(this.config.additionalCutVariantId);
        if (firstFromSetting && additionalFromSetting) {
          if (Number(firstFromSetting.price) < Number(additionalFromSetting.price)) {
            return { firstVariant: additionalFromSetting, additionalVariant: firstFromSetting };
          }
          return { firstVariant: firstFromSetting, additionalVariant: additionalFromSetting };
        }

        const hasToken = (variant, token) => {
          const needle = String(token || '').toUpperCase();
          return String(variant?.sku || '').toUpperCase().includes(needle) || String(variant?.title || '').toUpperCase().includes(needle);
        };

        let firstVariant =
          firstFromSetting ||
          variants.find((variant) => hasToken(variant, 'S1010') && Number(variant.price) === 3000) ||
          variants.find((variant) => Number(variant.price) === 3000) ||
          null;

        let additionalVariant =
          additionalFromSetting ||
          variants.find((variant) => hasToken(variant, 'S1010') && Number(variant.price) === 500) ||
          variants.find((variant) => Number(variant.price) === 500) ||
          null;

        if ((!firstVariant || !additionalVariant) && variants.length >= 2) {
          const sorted = [...variants].sort((a, b) => Number(a.price) - Number(b.price));
          const cheapest = sorted[0] || null;
          const mostExpensive = sorted[sorted.length - 1] || null;

          if (!firstVariant) firstVariant = mostExpensive;
          if (!additionalVariant) additionalVariant = cheapest;
        }

        if (firstVariant && additionalVariant && Number(firstVariant.id) === Number(additionalVariant.id)) {
          return { firstVariant, additionalVariant: null };
        }

        if (firstVariant && additionalVariant && Number(firstVariant.price) < Number(additionalVariant.price)) {
          return { firstVariant: additionalVariant, additionalVariant: firstVariant };
        }

        return { firstVariant, additionalVariant };
      }

      createCutOrderId() {
        return `ZS-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
      }

      getBackendOptionName(name) {
        const normalized = String(name || '').toLowerCase();
        if (/abmessung|dimension|dimensi[oó]n|dimensione|wymiar/.test(normalized)) return 'Abmessung';
        if (/farbe|color|colour|couleur|kolor|colore/.test(normalized)) return 'Farbe';
        if (/material/.test(normalized)) return 'Material';
        if (/oberfl[aä]che|surface|superficie|powierzchnia/.test(normalized)) return 'Oberfläche';
        return name;
      }

      formatPieceCounts(pieces) {
        const counts = pieces.reduce((items, piece) => {
          items[piece] = (items[piece] || 0) + 1;
          return items;
        }, {});

        return Object.entries(counts)
          .map(([length, quantity]) => `${quantity}x ${length} mm`)
          .join(', ');
      }

      formatProductionLine(bar, labels = this.config.labels) {
        const quantity = bar.quantity || 1;
        const pieces = this.formatPieceCounts(bar.pieces);
        const profileLabel = this.result?.profile?.title || 'Profil';
        const unit = quantity > 1 ? 'identische Profile' : 'Profil';
        const prefix = `${quantity}x ${unit}`;

        const restText = bar.restPieces ? this.formatPieceCounts(bar.restPieces) : `${bar.rest} mm`;
        return `${prefix} ${profileLabel} ${bar.variant.length} mm: ${pieces} | ${labels.rest || 'Rest'} ${restText}${bar.restCuts ? ` (+${bar.restCuts} Restschnitte)` : ''} | ${labels.kerfLoss || 'Schnittverlust'} ${bar.kerfLoss} mm ${labels.per || 'je'} ${profileLabel}`;
      }

      createCutPlanText(cutOrderId) {
        const labels = this.config.backendLabels;
        const selected = this.result.selectedOptions.map((option) => `  ${this.getBackendOptionName(option.name)}: ${option.value}`);
        const details = this.result.bars.map((bar, index) => `  ${index + 1}. ${this.formatProductionLine(bar, labels)}`);

        return [
          `${labels.cutOrder.toUpperCase()} ${cutOrderId}`,
          '',
          labels.profile.toUpperCase(),
          `  ${labels.article}: ${this.result.profile.title}`,
          ...selected,
          '',
          labels.rawItemsHeading.toUpperCase(),
          `  ${this.formatBars(this.result.bars)}`,
          '',
          labels.orderedCutsHeading.toUpperCase(),
          `  ${this.formatCuts(this.result.cuts)}`,
          '',
          labels.sumsHeading.toUpperCase(),
          `  Schnittfuge: ${this.config.kerf} mm`,
          `  ${labels.totalCuts}: ${this.result.cutCount}`,
          `  ${labels.kerfLoss} gesamt: ${this.result.kerfLoss} mm`,
          `  ${labels.totalRest}: ${this.result.rest} mm`,
          `  ${labels.unusedTotal} gesamt: ${this.result.unused} mm (${this.formatNumber(this.result.wastePercent)} %)`,
          '',
          labels.planHeading.toUpperCase(),
          ...details,
          '',
          labels.noteHeading.toUpperCase(),
          `  ${labels.piecesIncluded}`,
        ].join('\n');
      }

      groupCartProfileItems(selectedProperties, cutOrderId, cutMetaProperties = {}) {
        const labels = this.config.backendLabels;
        const groups = new Map();

        this.result.bars.forEach((bar) => {
          const key = String(bar.variant.id);
          const quantity = bar.quantity || 1;

          if (!groups.has(key)) {
            groups.set(key, {
              id: Number(bar.variant.id),
              variant: bar.variant,
              quantity: 0,
            });
          }

          const group = groups.get(key);
          group.quantity += quantity;
        });

        return Array.from(groups.values()).map((group) => ({
          id: group.id,
          quantity: group.quantity,
          properties: {
            _cut_order: cutOrderId,
            _cut_mode: 'profile',
            _cut_kind: 'material',
            ...cutMetaProperties,
          },
        }));
      }

      createOrderNoteText(cutOrderId) {
        if (!this.result) return '';

        const planPayload = {
          v: 1,
          mode: 'profile',
          profileHandle: String(this.result.profile?.handle || ''),
          selectedValues: this.getSelectedFilterValues(),
          cuts: this.result.cuts,
        };
        const planCode = this.encodePlanCode(planPayload);
        const planLink = this.buildPlanLink(planCode);

        const optionText = this.result.selectedOptions
          .map((option) => `${option.name}: ${option.value}`)
          .filter(Boolean)
          .join(', ');

        const language = String(this.config.language || document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
        const noteI18n = {
          de: { article: 'Profil', raw: 'Rohmaterial', cuts: 'Zuschnitte', plan: 'Plan' },
          en: { article: 'Profile', raw: 'Raw material', cuts: 'Cuts', plan: 'Plan' },
          fr: { article: 'Profil', raw: 'Mati\u00e8re brute', cuts: 'D\u00e9coupes', plan: 'Plan' },
          es: { article: 'Perfil', raw: 'Material en bruto', cuts: 'Cortes', plan: 'Plano' },
          it: { article: 'Profilo', raw: 'Materiale grezzo', cuts: 'Tagli', plan: 'Piano' },
          pl: { article: 'Profil', raw: 'Materia\u0142', cuts: 'Ci\u0119cia', plan: 'Plan' },
        };
        const nl = noteI18n[language] || noteI18n.de;

        const header = `ZUSCHNITT ${cutOrderId}`;
        const article = `${nl.article}: ${this.result.profile.title}${optionText ? ` / ${optionText}` : ''}`;
        const raw = `${nl.raw}: ${this.formatBars(this.result.bars)}`;
        const cuts = `${nl.cuts}: ${this.formatCuts(this.result.cuts)}`;

        return [
          header,
          article,
          raw,
          cuts,
          planLink ? `${nl.plan}: ${planLink}` : null,
        ]
          .filter(Boolean)
          .join('\n');
      }

      async addToCart() {
        if (!this.result) return;
        const orderResult = this.result;
        const labels = this.config.backendLabels;

        const { firstVariant, additionalVariant } = this.resolveServiceVariants();
        if (this.result.cutCount > 0 && (!firstVariant || (this.result.cutCount > 1 && !additionalVariant))) {
          this.renderEmpty(this.config.labels.messageMissingService || 'Bitte das Serviceprodukt S1010 (Erstschnitt/Folgeschnitt) in den Section-Einstellungen hinterlegen (oder Variant IDs eintragen).', 'error');
          return;
        }

        const selectedProperties = this.result.selectedOptions.reduce((properties, option) => {
          properties[this.getBackendOptionName(option.name)] = option.value;
          return properties;
        }, {});
        const cutOrderId = this.createCutOrderId();

        const materialCutMeta = {
          _cut_total_cuts: String(this.result.cutCount),
          _cut_service_first_variant_id: firstVariant ? String(firstVariant.id) : '',
          _cut_service_add_variant_id: additionalVariant ? String(additionalVariant.id) : '',
          _cut_service_first_qty: this.result.cutCount > 0 ? '1' : '0',
          _cut_service_add_qty: String(Math.max(0, this.result.cutCount - 1)),
        };

        const items = this.groupCartProfileItems(selectedProperties, cutOrderId, materialCutMeta);
        const noteText = this.createOrderNoteText(cutOrderId);

        if (this.result.cutCount > 0) items.push({
          id: firstVariant.id,
          quantity: 1,
          properties: {
            _cut_order: cutOrderId,
            _cut_mode: 'profile',
            _cut_kind: 'service',
          },
        });

        if (this.result.cutCount > 1) {
          items.push({
            id: additionalVariant.id,
            quantity: this.result.cutCount - 1,
            properties: {
              _cut_order: cutOrderId,
              _cut_mode: 'profile',
              _cut_kind: 'service',
            },
          });
        }

        this.addCartButton.disabled = true;
        this.addCartButton.classList.add('loading');
        const unlockControls = window.FibroluxCutDelivery.lock(this);

        try {
          await window.FibroluxCutDelivery.verify(this, orderResult);
          await window.FibroluxCutShipping.prepare(this, orderResult, items);
          const pdfHelper = window.FibroluxCutPlanPdf;
          if (!pdfHelper) throw new Error('pdf_helper_missing');
          const pdfBlob = await pdfHelper.createProfilePdf({
            result: orderResult,
            planId: cutOrderId,
            language: this.config.language,
            kerf: this.config.kerf,
            pdfLibraryUrl: this.config.pdfLibraryUrl,
          });
          const pdfOwnerIndex = items.findIndex((item) => item.properties?._cut_kind === 'service');
          await pdfHelper.addOrderToCartWithPdf({
            cartAddUrl: this.config.cartAddUrl,
            items,
            ownerIndex: pdfOwnerIndex >= 0 ? pdfOwnerIndex : 0,
            pdfBlob,
            fileName: `FIBROLUX-${cutOrderId}-Profil-Zuschnittsplan.pdf`,
            ownerProperties: {
              ...window.FibroluxCutDelivery.properties(orderResult),
              'Plan-ID': cutOrderId,
              Planart: orderResult.cutCount > 0 ? 'Profilzuschnitt' : 'Standardprofile ohne Zuschnitt',
              Planübersicht: `${orderResult.pieces.length} Fertigteile / ${orderResult.bars.reduce((sum, bar) => sum + Math.max(1, Number(bar.quantity || 1)), 0)} Rohprofile / ${orderResult.cutCount} Schnitte`,
            },
          });
          await this.markCartAsCutOrder(cutOrderId, noteText);
          window.location.href = this.config.cartUrl;
        } catch (error) {
          console.error('Profil-Zuschnitt: Plan-PDF oder Warenkorbposition konnte nicht erstellt werden.', error);
          this.renderEmpty(error.deliveryChoice ? error.message : this.config.labels.messageCartError || 'Der Warenkorb konnte nicht aktualisiert werden. Bitte versuche es erneut.', 'error');
          this.addCartButton.disabled = false;
        } finally {
          unlockControls();
          this.addCartButton.classList.remove('loading');
        }
      }

      async markCartAsCutOrder(cutOrderId, noteText = '') {
        const labels = this.config.backendLabels;
        const estimate = this.getCutDeliveryEstimateRange();
        const cartJsonUrl = `${String(this.config.cartUrl || '/cart')}.js`;

        const normalizeCutNote = (existingNote, activeCutOrderIds, newCutOrderId, newNoteText) => {
          const stripPlanCodeLines = (text) =>
            String(text || '')
              .split(/\r?\n/)
              .filter((line) => !/^Plan-Code:/i.test(String(line || '').trim()))
              .join('\n')
              .trimEnd();

          const active = new Set((activeCutOrderIds || []).filter(Boolean).map(String));
          const targetId = String(newCutOrderId || '');
          const noteStr = String(existingNote || '');

          const lines = noteStr.split(/\r?\n/);
          const prefixLines = [];
          const blocks = [];
          let current = null;

          for (const line of lines) {
            const match = line.match(/^ZUSCHNITT\s+(\S+)/);
            if (match) {
              if (current) blocks.push(current);
              current = { id: match[1], lines: [line] };
              continue;
            }

            if (current) current.lines.push(line);
            else prefixLines.push(line);
          }
          if (current) blocks.push(current);

          const prefix = prefixLines.join('\n').trimEnd();
          const nextBlocks = [];
          const seen = new Set();
          const replacement = String(newNoteText || '').trimEnd();

          for (const block of blocks) {
            const id = String(block.id || '');
            if (!id || !active.has(id) || seen.has(id)) continue;
            if (id === targetId && replacement) nextBlocks.push(replacement);
            else nextBlocks.push(stripPlanCodeLines(block.lines.join('\n')));
            seen.add(id);
          }

          if (targetId && active.has(targetId) && !seen.has(targetId) && replacement) {
            nextBlocks.push(replacement);
          }

          const parts = [];
          if (prefix.trim()) parts.push(prefix.trim());
          nextBlocks.forEach((b) => {
            const t = String(b || '').trim();
            if (t) parts.push(t);
          });

          return parts.join('\n\n').trimEnd();
        };

        let note = '';
        let shouldUpdateNote = false;
        try {
          const cartResponse = await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } });
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            const existing = String(cart?.note || '');
            const activeIds = (cart?.items || [])
              .map((it) => it?.properties?._cut_order)
              .filter(Boolean)
              .map(String);

            const normalized = normalizeCutNote(existing, activeIds, cutOrderId, noteText);
            shouldUpdateNote = normalized !== existing.trimEnd();
            note = normalized;
          }
        } catch (error) {
          // ignore
        }
        try {
          await fetch(this.config.cartUpdateUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              ...(shouldUpdateNote ? { note } : {}),
              attributes: {
                [labels.cartAttributeCutOrder]: 'Ja',
                [labels.cartAttributeLastCutOrder]: cutOrderId,
                [labels.cartAttributeDeliveryTime]: estimate?.dateText || '',
              },
            }),
          });
        } catch (error) {
          console.warn('Cut order attribute could not be saved.', error);
        }
      }
    }

    customElements.define('cut-configurator', CutConfigurator);
  })();
